<template>
  <!-- Marker: styled like the timeline marker, no icon -->
  <div
    v-if="isMarker"
    class="show-library__marker"
    :style="markerStyle"
    :data-test="`producer-showLibrary-marker:${itemId}`"
  >
    <span class="show-library__marker-line" />
    <span class="show-library__marker-title" :title="title">{{ title || 'Marker' }}</span>
    <span class="show-library__marker-line" />
  </div>

  <!-- Graphic group: nested card of items -->
  <div
    v-else-if="isGroup"
    class="show-library__card show-library__card--group"
    :data-test="`producer-showLibrary-group:${itemId}`"
  >
    <div class="show-library__card-row">
      <div class="tb-checkbox show-library__select-all">
        <input
          type="checkbox"
          :id="`group-all-${showId}-${itemId}`"
          :checked="isGroupAllSelected"
          :aria-label="`Select all items in ${title || `this ${groupNoun}`}`"
          :data-test="`producer-showLibrary-group-selectAll:${itemId}`"
          @change="toggleGroupAll"
        />
        <label :for="`group-all-${showId}-${itemId}`" />
      </div>

      <span class="far fa-image-stack show-library__card-icon" />
      <span class="show-library__card-title" :title="title">{{ title || `Untitled ${groupNoun}` }}</span>

      <button
        class="tb-btn tb-btn-contained sm medium-em add-to-playlist show-library__add"
        :title="`Add ${groupNoun} to timeline`"
        :data-test="`producer-showLibrary-group-add:${itemId}`"
        @click.stop="addGroupToTimeline"
      >
        <span class="fas fa-plus" />
      </button>
    </div>

    <div class="show-library__card-body">
      <draggable
        v-if="groupItems.length"
        :list="groupItems"
        :clone="cloneNestedItem"
        :sort="false"
        animation="200"
        :group="{ name: 'timeline', pull: 'clone', put: false }"
        revert-on-spill="true"
        data-test="producer-showLibrary-groupItemsList"
        @start="startNestedItemDrag"
        @end="onDragEnd"
      >
        <show-nested-item
          v-for="child in groupItems"
          :key="child.id"
          :item="child"
          :show-id="showId"
          :data-show-id="showId"
          :data-show-item-id="child.id"
          :data-test="`producer-showLibrary-groupItem:${child.id}`"
        />
      </draggable>
    </div>
  </div>

  <!-- Regular item: GlobalSearch-style resource row (no plus button) -->
  <div v-else class="source-item" :data-test="`producer-showLibrary-resource:${itemId}`">
    <div class="tb-checkbox">
      <input
        type="checkbox"
        :id="checkboxId"
        :checked="isSelected"
        :aria-label="`Select ${title || 'item'}`"
        :data-test="`producer-showLibrary-resource-checkBox:${itemId}`"
        @change="toggleSelected"
      />
      <label :for="checkboxId" />
    </div>

    <div class="source-item-wrapper">
      <img
        v-if="previewUrl"
        class="preview mr-2"
        :src="previewUrl"
        :alt="title"
        :data-test="`producer-showLibrary-resource-preview:${itemId}`"
      />

      <div class="source-item-info-wrapper min-w-0 flex-1">
        <div
          class="source-title title-text"
          :title="title"
          :data-test="`producer-showLibrary-resource-title:${itemId}`"
        >
          {{ title || 'Untitled item' }}
        </div>
      </div>

      <div class="source-description" :data-test="`producer-showLibrary-resource-type:${itemId}`">
        <span class="resource-type-label" :class="`resource-type-label--${typeClass}`">
          {{ typeLabel }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShowNestedItem',

  inject: ['showLibrary'],

  props: {
    item: {
      type: Object,
      required: true,
    },
    showId: {
      type: [Number, String],
      required: true,
    },
  },

  computed: {
    data() {
      return this.item.data || this.item;
    },

    isMarker() {
      return this.data.resourceType === 'marker';
    },

    isGroup() {
      return this.data.resourceType === 'graphic-group';
    },

    groupNoun() {
      return 'Custom Scene';
    },

    groupItems() {
      return this.data.items || [];
    },

    title() {
      return this.data.title || '';
    },

    itemId() {
      return this.item.id ?? this.item.itemId ?? this.data.resourceId ?? 'unknown';
    },

    checkboxId() {
      return `show-resource-${this.showId}-${this.itemId}`;
    },

    isSelected() {
      return this.showLibrary.isItemSelected(this.item);
    },

    previewUrl() {
      return this.showLibrary.itemPreviewUrl(this.item);
    },

    typeLabel() {
      return this.showLibrary.itemTypeLabel(this.item);
    },

    typeClass() {
      return this.data.resourceType || 'item';
    },

    markerStyle() {
      return this.showLibrary.markerStyle(this.item);
    },

    isGroupAllSelected() {
      return this.showLibrary.isGroupAllSelected(this.item);
    },
  },

  methods: {
    toggleSelected() {
      this.showLibrary.toggleSelected(this.item);
    },

    toggleGroupAll() {
      this.showLibrary.toggleGroupAll(this.item);
    },

    addGroupToTimeline() {
      this.showLibrary.addGroupToTimeline(this.item);
    },

    cloneNestedItem(child) {
      return this.showLibrary.cloneNestedItem(child);
    },

    startNestedItemDrag(e) {
      this.showLibrary.startNestedItemDrag(e);
    },

    onDragEnd() {
      this.showLibrary.onDragEnd();
    },
  },
};
</script>

<style lang="scss" scoped>
.show-library {
  &__card {
    display: flex;
    padding: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.375rem;
    align-self: stretch;
    border-radius: 6px;
    border: 1px solid rgba(66, 66, 66, 0.5);
    background: #262626;
    cursor: grab;
  }

  &__card--group {
    // The parent component's shared `.show-library__card` border rule also
    // matches this element (child component roots carry the parent's scope
    // id), so the compound selector is needed to win the cascade.
    &.show-library__card {
      border-style: dashed;
    }
  }

  &__card-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    align-self: stretch;
    min-width: 0;

    .show-library__card-title {
      flex: 1 1 auto;
    }
  }

  &__card-icon {
    flex-shrink: 0;
    color: #a5a5a5;
    font-size: 0.8125rem;
  }

  &__card-title {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #fff;
    font-weight: 500;
    font-size: 0.75rem;
  }

  &__card-body {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    align-self: stretch;
    min-width: 0;
  }

  &__select-all {
    flex-shrink: 0;
    margin: 0;
    display: inline-flex;
    align-items: center;

    // Must match the specificity of the global `.tb-checkbox input + label`
    // rule or the default padding/gutters win the cascade. The label is sized
    // to the box so the flex gap separates it from the next header element.
    input[type='checkbox'] + label {
      width: 1.5em;
      min-height: 1.5em;
      line-height: 1;
      padding-left: 0;
      font-size: 0.6875rem;
    }
  }

  &__add {
    flex-shrink: 0;
  }

  &__marker {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    align-self: stretch;
    padding: 5px 10px;
    cursor: grab;

    &-line {
      height: 1px;
      flex: 1;
      min-width: 1.5rem;
      background: var(--marker-color, #CC9F10);
    }

    &-title {
      color: var(--marker-color, #CC9F10);
      font-weight: 600;
      white-space: nowrap;
    }
  }
}

// Match the resource card styling used by GlobalSearch.vue (rounded corners,
// fixed height, and the same background) instead of the default source list
// card.
.source-item .source-item-wrapper {
  padding: 8px;
  height: 50px;
  border-radius: 6px;
  background: #2C2C2C;
}

.source-item .preview {
  aspect-ratio: 16 / 9;
  border-radius: 2px;
  flex-shrink: 0;
}

// Ensure resource names stay on a single line with ellipsis, even when the
// card is narrow or the title is long.
.source-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resource-type-label {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  font-style: italic;
  letter-spacing: 0.5px;
  white-space: nowrap;
  &--graphic {
    color: #0095a9;
  }

  &--panel {
    color: #607d8b;
  }

  &--theme {
    color: #009688;
  }

  &--poll {
    color: #A30065;
  }

  &--display {
    color: #A30065;
  }

  &--asset {
    color: #fde68a;
  }
}
</style>
